import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Chip, IconButton, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tooltip, Typography, Pagination,
} from '@mui/material';
import { Add as AddIcon, Visibility } from '@mui/icons-material';
import { GoodsReceiptService } from '../../services/goodsReceipt.service';
import { GoodsReceipt, GoodsReceiptStatus } from '../../interfaces/procurement/goodsReceipt.interface';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';

// GoodsReceiptController.create's @PreAuthorize: StoreAdmin, InstitutionAdmin. InstitutionManager can
// list receipts but would get a 403 creating one.
const CREATE_ROLES = ['StoreAdmin', 'InstitutionAdmin'];

const STATUS_COLORS: Record<GoodsReceiptStatus, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  DRAFT: 'default',
  POSTED: 'success',
  REVERSED: 'error',
};

export const GoodsReceiptList: React.FC = () => {
  const navigate = useNavigate();
  const rawRoles = AuthService.getCurrentUser()?.roles;
  const userRoles: string[] = Array.isArray(rawRoles) ? rawRoles : rawRoles ? [rawRoles] : [];
  const canCreate = userRoles.some(r => CREATE_ROLES.includes(r));
  const toast = useToast();
  const [grns, setGrns] = useState<GoodsReceipt[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageInfo, setPageInfo] = useState({ currentPage: 0, totalPages: 0, totalElements: 0 });

  const fetchGRNs = async (page = 0) => {
    try {
      setLoading(true);
      const response = await GoodsReceiptService.listByInstitution(page, 10);
      setGrns(response.content);
      setPageInfo({ currentPage: response.number, totalPages: response.totalPages, totalElements: response.totalElements });
    } catch (e) {
      toast.showApiError(e, 'Failed to load goods receipts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGRNs(0);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    fetchGRNs(page - 1);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Goods Receipts</Typography>
        {canCreate && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/procurement/goods-receipts/new')}>
            New Goods Receipt
          </Button>
        )}
      </Box>

      <Typography variant="body2" sx={{ mb: 1 }}>Total: {pageInfo.totalElements}</Typography>

      {loading ? (
        <Typography>Loading...</Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>GRN Number</TableCell>
                <TableCell>PO Number</TableCell>
                <TableCell>Receipt Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Total Value</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {grns.map(grn => (
                <TableRow key={grn.id}>
                  <TableCell>{grn.grnNumber}</TableCell>
                  <TableCell>{grn.purchaseOrderId}</TableCell>
                  <TableCell>{grn.receiptDate}</TableCell>
                  <TableCell>
                    <Chip label={grn.status} color={STATUS_COLORS[grn.status]} size="small" />
                  </TableCell>
                  <TableCell>{grn.totalValue}</TableCell>
                  <TableCell>
                    <Tooltip title="View" arrow>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => navigate(`/procurement/goods-receipts/${grn.id}`)}
                        aria-label="View"
                      >
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {pageInfo.totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={pageInfo.totalPages}
            page={pageInfo.currentPage + 1}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      )}
    </Box>
  );
};

export default GoodsReceiptList;
